import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { FEATURES } from "@/config/features";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TradesIndex from "@/pages/TradesIndex";
import TradePage, { OverheadsRedirect } from "@/pages/TradePage";
import EstimatorPage from "@/pages/EstimatorPage";
import AssistantPage from "@/pages/AssistantPage";
import DashboardPage from "@/pages/DashboardPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ReceiptsPage from "@/pages/ReceiptsPage";
import VatReturnPage from "@/pages/VatReturnPage";
import TimesheetsPage from "@/pages/TimesheetsPage";
import NotesPage from "@/pages/NotesPage";
import BeforeAfterPage from "@/pages/BeforeAfterPage";
import CollagePage from "@/pages/CollagePage";
import BeforeAfterVideoPage from "@/pages/BeforeAfterVideoPage";
import GalleryPage from "@/pages/GalleryPage";
import DesignVisualiserPage from "@/pages/DesignVisualiserPage";
import PlanDesignerPage from "@/pages/PlanDesignerPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceEditorPage from "@/pages/InvoiceEditorPage";
import InvoicePublicPage from "@/pages/InvoicePublicPage";
import BusinessPage from "@/pages/BusinessPage";
import SettingsPage from "@/pages/SettingsPage";
import PricingPage from "@/pages/PricingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import NotePublicPage from "@/pages/NotePublicPage";
import PortfolioPublicPage from "@/pages/PortfolioPublicPage";
import PlanPublicPage from "@/pages/PlanPublicPage";
import FeedbackPage from "@/pages/FeedbackPage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AboutPage from "@/pages/AboutPage";
import LegalPage from "@/pages/LegalPage";
import CalculatorsIndex from "@/pages/CalculatorsIndex";
import CalculatorPage from "@/pages/CalculatorPage";
import GuidesIndex from "@/pages/GuidesIndex";
import FuelPage from "@/pages/FuelPage";
import PlantHirePage from "@/pages/PlantHirePage";
import WorkspacePage from "@/pages/WorkspacePage";
import CustomersPage from "@/pages/CustomersPage";
import FuelLocationPage from "@/pages/FuelLocationPage";
import MarketplacePage from "@/pages/MarketplacePage";
import VehicleCheckPage from "@/pages/VehicleCheckPage";
import GuidePage from "@/pages/GuidePage";
import TrialPage from "@/pages/TrialPage";
import usePageTracker from "@/lib/usePageTracker";

function PageTracker() {
  usePageTracker();
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PageTracker />
        <Routes>
          {/* Public, no nav/footer */}
          <Route path="/q/:token" element={<PublicQuotePage />} />
          <Route path="/n/:token" element={<NotePublicPage />} />
          <Route path="/portfolio/:token" element={<PortfolioPublicPage />} />
          <Route path="/p/:token" element={<PlanPublicPage />} />
          <Route path="/i/:token" element={<InvoicePublicPage />} />

          <Route element={<Layout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/trial/:code" element={<TrialPage />} />
            <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
            <Route path="/trades" element={<TradesIndex />} />
            <Route path="/trades/:slug" element={<TradePage />} />
            <Route path="/calculators" element={<CalculatorsIndex />} />
            <Route path="/calculators/:slug" element={<CalculatorPage />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/fuel" element={<FuelPage />} />
            <Route path="/plant-hire" element={<PlantHirePage />} />
            <Route path="/fuel/:location" element={<FuelLocationPage />} />
            <Route path="/shop" element={<Navigate to="/" replace />} />
            <Route path="/marketplace" element={FEATURES.marketplace ? <MarketplacePage /> : <Navigate to="/" replace />} />
            <Route path="/vehicle-check" element={FEATURES.vehicleCheck ? <VehicleCheckPage /> : <Navigate to="/" replace />} />
            <Route path="/estimator" element={<EstimatorPage />} />
            <Route path="/estimator/:id" element={<ProtectedRoute><EstimatorPage /></ProtectedRoute>} />
            <Route path="/assistant" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
            <Route path="/overheads" element={<OverheadsRedirect />} />
            <Route path="/workspace" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
            <Route path="/jobs/:id" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
            <Route path="/receipts" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />
            <Route path="/vat-return" element={<ProtectedRoute><VatReturnPage /></ProtectedRoute>} />
            <Route path="/timesheets" element={<ProtectedRoute><TimesheetsPage /></ProtectedRoute>} />
            <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
            <Route path="/before-after" element={<ProtectedRoute><BeforeAfterPage /></ProtectedRoute>} />
            <Route path="/collage" element={<ProtectedRoute><CollagePage /></ProtectedRoute>} />
            <Route path="/before-after-video" element={<ProtectedRoute><BeforeAfterVideoPage /></ProtectedRoute>} />
            <Route path="/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
            <Route path="/design" element={<ProtectedRoute><DesignVisualiserPage /></ProtectedRoute>} />
            <Route path="/plan-designer" element={<ProtectedRoute><PlanDesignerPage /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceEditorPage /></ProtectedRoute>} />
            <Route path="/business" element={<ProtectedRoute><BusinessPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/legal/:slug" element={<LegalPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
